import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { toast } from "@volca/ui/lib/toast";
import { useBusinessSettingsSidebar } from "@/business-settings/context/business-settings-sidebar-context";
import { useDeleteUser } from "@/business-settings/users/context/delete-user-context";
import { getErrorMessage } from "@/core/api/errors";
import { useBusiness } from "@/core/business";
import { getUserFullName } from "@/core/utils/name";
import { useDeleteUser as useDeleteUserMutation } from "@/user/query";
import { DetailedUser } from "@/user/user";

interface DeleteUserConfirmationModalProps {
  users: DetailedUser[];
}

export const DeleteUserConfirmationModal = ({
  users,
}: DeleteUserConfirmationModalProps) => {
  const { businessId } = useBusiness();
  const { deleteUserId, setDeleteUserId } = useDeleteUser();
  const { resetSidebar } = useBusinessSettingsSidebar();
  const { mutate: deleteUser, isPending: isDeleting } =
    useDeleteUserMutation(businessId);

  const userToDelete = users.find((user) => user.id === deleteUserId);
  const isOpen = !!userToDelete;
  const userName = userToDelete
    ? getUserFullName(userToDelete) || userToDelete.email
    : "";

  const handleConfirm = () => {
    if (!deleteUserId) return;

    deleteUser(deleteUserId, {
      onSuccess: () => {
        toast.success("User deleted successfully");
        setDeleteUserId(null);
        resetSidebar();
      },
      onError: (error) => {
        toast.error("Failed to delete user", {
          description: getErrorMessage(error),
        });
      },
    });
  };

  const handleCancel = () => {
    setDeleteUserId(null);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleCancel}>
      <DialogContent>
        <DialogDescription className="sr-only">
          {`Are you sure you want to delete ${userName}? This action cannot be undone.`}
        </DialogDescription>
        <DialogHeader>
          <DialogTitle>Delete user</DialogTitle>
        </DialogHeader>
        <Stack align="start" className="w-full" gap={6}>
          <Text tone="muted">
            Are you sure you want to delete <b>{userName}</b>? This action
            cannot be undone.
          </Text>
        </Stack>
        <DialogFooter>
          <Button
            variant="secondary"
            onClick={handleCancel}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            color="danger"
            onClick={handleConfirm}
            disabled={isDeleting}
            loading={isDeleting}
          >
            Delete user
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
